import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_DOMAIN } from 'consts';
import { currentUserKey } from 'hooks/use-current-user';
import isString from 'lodash/isString';
import { security } from 'shared';

/** GET/POST/DELETE /api/zoom/link — the user's Zoom Team Chat linkage. */
export type ZoomLink = {
  linked: boolean;
  /** The user's Zoom JID (`…@xmpp.zoom.us`) once linked. */
  zoom_user_jid: string | null;
  verified_at: string | null;
  /** Six digits while a link is pending; the user sends it to the bot in Zoom. */
  link_code: string | null;
  link_code_expires_at: string | null;
  /** The bot's name as it appears in Zoom's Apps list. */
  bot_name: string;
};

export const ZOOM_LINK_KEY = ['zoom-link'];

async function authedFetch (path: string, init: globalThis.RequestInit = {}): Promise<Response> {
  const token = await security.getAccessTokenSilently()();
  return fetch(`${API_DOMAIN}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init.headers || {}),
    },
  });
}

async function readLink (resp: Response, fallback: string): Promise<ZoomLink> {
  if (!resp.ok) {
    let message = `${fallback}: ${resp.status}`;
    try {
      const parsed = await resp.json();
      if (parsed && isString(parsed.error)) message = parsed.error;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(message);
  }
  return resp.json();
}

/**
 * Link status. While a code is pending the dialog polls so it flips to
 * "linked" on its own once the code arrives from Zoom.
 */
export function useZoomLink (options?: { enabled?: boolean; pollWhilePending?: boolean }) {
  return useQuery({
    queryKey: ZOOM_LINK_KEY,
    queryFn: async () => readLink(await authedFetch('/api/zoom/link'), 'Failed to load Zoom status'),
    enabled: options?.enabled ?? true,
    refetchInterval: (query) => {
      const data = query.state.data;
      return options?.pollWhilePending && data && !data.linked && data.link_code ? 4000 : false;
    },
  });
}

export function useStartZoomLink () {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      readLink(await authedFetch('/api/zoom/link', { method: 'POST' }), 'Failed to create link code'),
    onSuccess: (data) => queryClient.setQueryData(ZOOM_LINK_KEY, data),
  });
}

export function useUnlinkZoom () {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      readLink(await authedFetch('/api/zoom/link', { method: 'DELETE' }), 'Failed to unlink'),
    onSuccess: (data) => {
      queryClient.setQueryData(ZOOM_LINK_KEY, data);
      // /api/user/current carries zoom_user_jid too.
      queryClient.invalidateQueries({ queryKey: currentUserKey });
    },
  });
}
